import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { SharedProfileContent } from '@/features/shared-profile/components/SharedProfileContent';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { useStartupSharePreview } from '../../hooks/useStartupSharePreview';

const S = UI_STRINGS.STARTUP_PROFILE;

/**
 * "Preview as investor" (feedback call 2026-10-26): the founder's profile
 * rendered by the very component a share-link recipient gets, fed by the same
 * redacted DTO, under a banner saying it is a preview. Reusing
 * `SharedProfileContent` rather than hiding buttons on the owner's page is
 * what keeps the preview honest: hidden empty fields and redactions match.
 *
 * @param entityId - the founder's own startup entity
 */
export function StartupSharePreview({ entityId }: { entityId: string }) {
  const preview = useStartupSharePreview(entityId);

  return (
    <div className="bg-page-bg min-h-full">
      <div
        role="status"
        className="border-divider flex flex-wrap items-center justify-between gap-3 border-b bg-white px-4 py-3 sm:px-8"
      >
        <p className="text-navy text-sm font-medium">{S.PREVIEW_BANNER}</p>
        <Button asChild variant="outline" className="inline-flex">
          <Link to={ROUTES.STARTUP_PROFILE}>{S.PREVIEW_BACK}</Link>
        </Button>
      </div>
      {preview.isLoading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}
      {preview.isError && (
        <div className="mx-auto max-w-4xl px-4 py-8">
          <ErrorState title={S.PREVIEW_ERROR} onRetry={() => void preview.refetch()} />
        </div>
      )}
      {preview.data && <SharedProfileContent profile={preview.data} />}
    </div>
  );
}
